import { useFormikContext } from "formik";
import { ArrowRight, Info } from "lucide-react";
import type { ReactNode } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FormikCell, FormikRadioYN } from "@/features/forms/components/formik-controls";
import type { Form8379Data } from "@/features/forms/types";

function QuestionRow({
  n,
  label,
  children,
}: {
  n: string;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-b py-4 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <div className="text-foreground text-sm">
        <span className="text-muted-foreground mr-2 font-mono">{n}</span>
        {label}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

/**
 * Part I — Eligibility (legacy `isa_part1.php`). Questions 1–9, the conditional
 * community-property state name (Q5 = Y), and a non-gating soft advisory when the
 * answers suggest an injured-spouse claim may not apply (§9). Eligibility never
 * blocks PDF generation.
 */
export function IsaPart1({ onNext }: { onNext: () => void }) {
  const { values } = useFormikContext<Form8379Data>();
  const showCommProp = values.resd_comm_prop_chk === "Y";
  const advisory = values.file_joint_ret === "N" || values.legally_oblig_pay === "Y";

  return (
    <div className="flex flex-col gap-6">
      {advisory && (
        <Alert>
          <Info />
          <AlertDescription>
            These answers suggest the injured-spouse claim may not apply (a joint
            return is required, and you can't be legally obligated to pay the
            past-due amount). You can still complete and file the form.
          </AlertDescription>
        </Alert>
      )}

      <Card className="p-6">
        <QuestionRow n="1" label="Tax year(s) for which you are filing this form">
          <FormikCell name="tax_year" className="w-40" placeholder="e.g. 2022" maxLength={50} />
        </QuestionRow>
        <QuestionRow n="2" label="Did you file a joint return?">
          <FormikRadioYN name="file_joint_ret" aria-label="Filed a joint return" />
        </QuestionRow>
        <QuestionRow
          n="3"
          label="Did the IRS use the joint overpayment to pay a past-due amount?"
        >
          <FormikRadioYN name="joint_overpayment" aria-label="IRS used the joint overpayment" />
        </QuestionRow>
        <QuestionRow n="4" label="Are you legally obligated to pay this past-due amount?">
          <FormikRadioYN name="legally_oblig_pay" aria-label="Legally obligated to pay" />
        </QuestionRow>
        <QuestionRow n="5" label="Were you a resident of a community-property state?">
          <FormikRadioYN name="resd_comm_prop_chk" aria-label="Resident of a community-property state" />
        </QuestionRow>
        {showCommProp && (
          <QuestionRow n="5a" label="Name of the community-property state">
            <FormikCell name="res_comm_prop" className="w-64" maxLength={150} />
          </QuestionRow>
        )}
        <QuestionRow
          n="6"
          label="Did you make and report payments (e.g. federal income tax withheld / estimated)?"
        >
          <FormikRadioYN name="make_report_pay" aria-label="Made and reported payments" />
        </QuestionRow>
        <QuestionRow
          n="7"
          label="Did you have earned income (wages/salaries/self-employment)?"
        >
          <FormikRadioYN name="earned_income" aria-label="Had earned income" />
        </QuestionRow>
        <QuestionRow
          n="8"
          label="Did you claim the earned-income credit / additional child tax credit?"
        >
          <FormikRadioYN name="claim_earn_inc" aria-label="Claimed the earned-income credit" />
        </QuestionRow>
        <QuestionRow n="9" label="Did you claim a refundable tax credit?">
          <FormikRadioYN name="claim_refund_tax" aria-label="Claimed a refundable tax credit" />
        </QuestionRow>
      </Card>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onNext}>
          Go to Part II <ArrowRight />
        </Button>
      </div>
    </div>
  );
}
